import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

interface StatCard {
  label: string;
  value: number;
  color: string;
  icon: any;
}

interface PieItem {
  name: string;
  value: number;
  color: string;
}

interface BarItem {
  name: string;
  value: number;
}

interface AreaItem {
  name: string;
  value: number;
}

interface SectionDashboardProps {
  stats: StatCard[];
  pieData: PieItem[];
  pieTitle: string;
  pie2Data?: PieItem[];
  pie2Title?: string;
  barData: BarItem[];
  barTitle: string;
  barColor?: string;
  bar2Data?: BarItem[];
  bar2Title?: string;
  bar2Color?: string;
  areaData: AreaItem[];
  areaTitle: string;
  extraBarData?: BarItem[];
  extraBarTitle?: string;
  extraBarColor?: string;
}

const PieBlock: React.FC<{ title: string; data: PieItem[] }> = ({ title, data }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="bg-white dark:bg-navy-900 p-5 rounded-2xl border border-neutral-100 dark:border-navy-800 shadow-sm">
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">{title}</p>
      {total > 0 ? (
        <div className="flex items-center gap-4">
          <div className="w-36 h-36">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={30} outerRadius={60} paddingAngle={3} dataKey="value">
                  {data.filter(d => d.value > 0).map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v: number, n: string) => [`${v} (${total ? Math.round(v / total * 100) : 0}%)`, n]} contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)', fontSize: 12, fontWeight: 700 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 flex-1">
            {data.filter(d => d.value > 0).map(d => (
              <div key={d.name} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 flex-1">{d.name}</span>
                <span className="text-[10px] font-black text-navy-950 dark:text-white">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400 text-center py-10">Sem dados</p>
      )}
    </div>
  );
};

const BarBlock: React.FC<{ title: string; data: BarItem[]; color: string; maxItems?: number; labelWidth?: number }> = ({ title, data, color, maxItems = 8, labelWidth = 90 }) => (
  <div className="bg-white dark:bg-navy-900 p-5 rounded-2xl border border-neutral-100 dark:border-navy-800 shadow-sm">
    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">{title}</p>
    {data.length > 0 ? (
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.slice(0, maxItems)} layout="vertical" margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={labelWidth} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)', fontSize: 12, fontWeight: 700 }} />
            <Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} barSize={16} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <p className="text-xs text-slate-400 text-center py-10">Sem dados</p>
    )}
  </div>
);

const SectionDashboard: React.FC<SectionDashboardProps> = ({ stats, pieData, pieTitle, pie2Data, pie2Title, barData, barTitle, barColor = '#3b82f6', bar2Data, bar2Title, bar2Color = '#8b5cf6', areaData, areaTitle, extraBarData, extraBarTitle, extraBarColor = '#10b981' }) => {
  const hasPie2 = !!(pie2Data && pie2Data.some(d => d.value > 0));
  const hasBar2 = !!(bar2Data && bar2Data.length > 0);

  const areaBlock = (
    <div className="bg-white dark:bg-navy-900 p-5 rounded-2xl border border-neutral-100 dark:border-navy-800 shadow-sm">
      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">{areaTitle}</p>
      {areaData.length > 0 ? (
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={areaData} margin={{ left: 0, right: 10, top: 5, bottom: 0 }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={barColor} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={barColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: '#94a3b8' }} />
              <YAxis hide domain={[0, 'auto']} />
              <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)', fontSize: 12, fontWeight: 700 }} />
              <Area type="monotone" dataKey="value" stroke={barColor} strokeWidth={3} fillOpacity={1} fill="url(#areaGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-xs text-slate-400 text-center py-10">Sem dados</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 px-2 pb-8">
      <div className={`grid grid-cols-2 gap-4 ${stats.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
        {stats.map((s) => (
          <div key={s.label} className="bg-white dark:bg-navy-900 p-5 rounded-2xl border border-neutral-100 dark:border-navy-800 shadow-sm flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-slate-50 dark:bg-navy-800 ${s.color}`}><s.icon size={20} /></div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{s.label}</p>
              <p className="text-2xl font-black dark:text-white tracking-tighter">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PieBlock title={pieTitle} data={pieData} />
        {hasPie2 ? <PieBlock title={pie2Title || 'Distribuição'} data={pie2Data!} /> : <BarBlock title={barTitle} data={barData} color={barColor} />}
        {areaBlock}
      </div>

      {hasPie2 && (
        <div className={`grid grid-cols-1 gap-4 ${hasBar2 ? 'lg:grid-cols-2' : ''}`}>
          <BarBlock title={barTitle} data={barData} color={barColor} />
          {hasBar2 && <BarBlock title={bar2Title || 'Volume'} data={bar2Data!} color={bar2Color} />}
        </div>
      )}
      {!hasPie2 && hasBar2 && (
        <BarBlock title={bar2Title || 'Volume'} data={bar2Data!} color={bar2Color} />
      )}

      {extraBarData && extraBarData.length > 0 && (
        <BarBlock title={extraBarTitle || 'Extra'} data={extraBarData} color={extraBarColor} maxItems={10} labelWidth={100} />
      )}
    </div>
  );
};

export default SectionDashboard;
